
import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Archive, RefreshCw, Search, X, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import specialInventoryService from '../services/specialInventoryService';
import toast from 'react-hot-toast';

const SpecialInventoryPage = () => {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission(['admin', 'logistica']);

    const [products, setProducts] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        purchasing_price: '',
        stock: 0,
        is_active: 1
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await specialInventoryService.getLocalProductsStats();
            if (response.success) {
                setProducts(response.data.products);
                setStats(response.data.totals);
            }
        } catch (error) {
            toast.error('Error al cargar productos locales');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                product_name: product.product_name,
                standard_price: product.standard_price,
                purchasing_price: product.purchasing_price,
                stock: product.stock,
                is_active: product.is_active
            });
        } else {
            setEditingProduct(null);
            setFormData({
                product_name: '',
                standard_price: '',
                purchasing_price: '',
                stock: 0,
                is_active: 1
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProduct) {
                await specialInventoryService.updateLocalProduct(editingProduct.id, formData);
                toast.success('Producto actualizado');
            } else {
                await specialInventoryService.createLocalProduct(formData);
                toast.success('Producto creado');
            }
            setShowModal(false);
            fetchProducts();
        } catch (error) {
            toast.error('Error al guardar producto');
        }
    };

    const handleUpdateStock = async (id, currentStock) => {
        const change = window.prompt('Ingrese el cambio en el inventario (ej: 5 para sumar, -3 para restar):');
        if (change === null) return;

        const changeValue = parseInt(change);
        if (isNaN(changeValue)) {
            toast.error('Valor no válido');
            return;
        }

        try {
            await specialInventoryService.updateStock(id, { quantity_change: changeValue });
            toast.success('Inventario actualizado');
            fetchProducts();
        } catch (error) {
            toast.error('Error al actualizar stock');
        }
    };

    const handleToggleActive = async (product) => {
        const newStatus = product.is_active ? 0 : 1;
        const confirmMsg = newStatus === 0 
            ? `¿Desea deshabilitar "${product.product_name}"? Ya no aparecerá en el POS.` 
            : `¿Desea habilitar "${product.product_name}"?`;
            
        if (!window.confirm(confirmMsg)) return;

        try {
            await specialInventoryService.updateLocalProduct(product.id, { 
                ...product, 
                is_active: newStatus 
            });
            toast.success(newStatus === 1 ? 'Producto habilitado' : 'Producto deshabilitado');
            fetchProducts();
        } catch (error) {
            toast.error('Error al cambiar estado del producto');
        }
    };

    const filteredProducts = products.filter(p =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.barcode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Package className="text-blue-600" />
                        Inventario de Productos Especiales
                    </h1>
                    <p className="text-gray-500">Productos no facturados por Siigo</p>
                </div>
                {canEdit && (
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Plus size={20} />
                        Nuevo Producto
                    </button>
                )}
            </div>

            {/* KPI Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* Valor del Inventario */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl shadow-sm border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-blue-700">Valor Inventario</div>
                            <Package className="text-blue-500" size={20} />
                        </div>
                        <div className="text-2xl font-bold text-blue-900">
                            {formatCurrency(stats.total_valor_inventario)}
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                            {stats.total_unidades || 0} unidades en stock
                        </div>
                    </div>

                    {/* Ingresos Potenciales */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl shadow-sm border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-green-700">Ingresos Potenciales</div>
                            <svg className="text-green-500" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="text-2xl font-bold text-green-900">
                            {formatCurrency(stats.total_ingresos_potenciales)}
                        </div>
                        <div className="text-xs text-green-600 mt-1">
                            Si se vende todo el stock
                        </div>
                    </div>

                    {/* Utilidad Potencial */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-5 rounded-xl shadow-sm border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-purple-700">Utilidad Potencial</div>
                            <svg className="text-purple-500" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        </div>
                        <div className="text-2xl font-bold text-purple-900">
                            {formatCurrency(stats.total_utilidad_potencial)}
                        </div>
                        <div className="text-xs text-purple-600 mt-1">
                            Ganancia estimada total
                        </div>
                    </div>

                    {/* Margen Promedio */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-5 rounded-xl shadow-sm border border-orange-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-orange-700">Margen Promedio</div>
                            <svg className="text-orange-500" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div className="text-2xl font-bold text-orange-900">
                            {parseFloat(stats.margen_promedio_ponderado || 0).toFixed(1)}%
                        </div>
                        <div className="text-xs text-orange-600 mt-1">
                            Rentabilidad ponderada
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm mb-6 flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o código..."
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={fetchProducts}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Actualizar"
                >
                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600">Código</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600">Producto</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Costo Unit.</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Precio Venta</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Stock</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Valor Inv.</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Utilidad/Unit</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Margen %</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-center">Estado</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr>
                                <td colSpan="10" className="px-6 py-10 text-center text-gray-500">Cargando...</td>
                            </tr>
                        ) : filteredProducts.length === 0 ? (
                            <tr>
                                <td colSpan="10" className="px-6 py-10 text-center text-gray-500">No se encontraron productos</td>
                            </tr>
                        ) : (
                            filteredProducts.map(product => (
                                <tr key={product.id} className={`transition-colors ${product.is_active ? 'hover:bg-gray-50' : 'bg-gray-50/50 opacity-60'}`}>
                                    <td className="px-6 py-4 font-mono text-sm text-blue-600">{product.barcode}</td>
                                    <td className="px-6 py-4 font-medium text-gray-800">{product.product_name}</td>
                                    <td className="px-6 py-4 text-right text-gray-600 text-sm">
                                        {formatCurrency(product.purchasing_price)}
                                    </td>
                                    <td className="px-6 py-4 text-right text-gray-700 font-medium">
                                        {formatCurrency(product.standard_price)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`font-bold ${product.stock <= 5 ? 'text-red-500' : 'text-green-600'}`}>
                                            {product.stock}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right text-blue-700 font-medium text-sm">
                                        {formatCurrency(product.valor_inventario)}
                                    </td>
                                    <td className="px-6 py-4 text-right text-purple-700 font-medium text-sm">
                                        {formatCurrency(product.utilidad_por_unidad)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${parseFloat(product.margen_rentabilidad_porcentaje) >= 30 ? 'bg-green-100 text-green-700' :
                                            parseFloat(product.margen_rentabilidad_porcentaje) >= 20 ? 'bg-yellow-100 text-yellow-700' :
                                                parseFloat(product.margen_rentabilidad_porcentaje) >= 0 ? 'bg-orange-100 text-orange-700' :
                                                    'bg-red-100 text-red-700'
                                            }`}>
                                            {parseFloat(product.margen_rentabilidad_porcentaje || 0).toFixed(1)}%
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {product.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            {canEdit ? (
                                                <>
                                                    <button
                                                        onClick={() => handleToggleActive(product)}
                                                        className={`p-2 rounded-lg transition-colors ${product.is_active ? 'text-gray-400 hover:bg-gray-100' : 'text-orange-600 hover:bg-orange-50'}`}
                                                        title={product.is_active ? 'Deshabilitar' : 'Habilitar'}
                                                    >
                                                        {product.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStock(product.id, product.stock)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Ajustar Stock"
                                                    >
                                                        <Archive size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(product)}
                                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                        title="Editar"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="p-2 text-gray-400" title="Sin permisos para editar">
                                                    <Lock size={18} />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingProduct ? 'Editar Producto' : 'Nuevo Producto Especial'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Producto</label>
                                <input
                                    required
                                    type="text"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.product_name}
                                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Precio Compra (opc)</label>
                                    <input
                                        type="number"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.purchasing_price}
                                        onChange={(e) => setFormData({ ...formData, purchasing_price: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Precio Venta</label>
                                    <input
                                        required
                                        type="number"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.standard_price}
                                        onChange={(e) => setFormData({ ...formData, standard_price: e.target.value })}
                                    />
                                </div>
                            </div>
                            {!editingProduct && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock Inicial</label>
                                    <input
                                        type="number"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.stock}
                                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                                    />
                                </div>
                            )}

                            {editingProduct && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                        checked={formData.is_active === 1}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                                    />
                                    <label htmlFor="is_active" className="text-sm font-bold text-gray-700 cursor-pointer">
                                        Producto Activo (Visible en POS)
                                    </label>
                                </div>
                            )}
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                                >
                                    {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecialInventoryPage;
